import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CheckmarkCircle02Icon,
  Cancel01Icon,
  ViewIcon,
  ViewOffIcon,
} from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuthStore } from "@/features/auth/stores/authStore";
import {
  useCallPassAPIInfos,
  useUpsertCallPassAPIInfo,
  useDecryptedApiInfo,
} from "../hooks/useCallpass";
import { encrypt } from "../utils/aesGcm";
import {
  CALL_CENTER_TYPES,
  CALL_CENTER_STATUS_MAP,
} from "../types/callpass.types";
import type {
  CallCenterType,
  CallPassAPIInfoDto,
  Cargo24Auth,
  OneCallAuth,
  InsungAuth,
  CargoManAuth,
} from "../types/callpass.types";

// --- Schemas ---

const cargo24Schema = z.object({
  Key: z.string().min(1, "API Key는 필수입니다"),
  AESKey: z.string().min(1, "AES Key는 필수입니다"),
  AESIV: z.string().min(1, "AES IV는 필수입니다"),
});

const oneCallSchema = z
  .object({
    Key: z.string().optional(),
    Id: z.string().optional(),
    Password: z.string().optional(),
  })
  .refine(
    (d) =>
      (d.Key && d.Key.length > 0) ||
      (d.Id && d.Id.length > 0 && d.Password && d.Password.length > 0),
    { message: "Key 또는 (Id + Password)를 입력하세요" },
  );

const insungSchema = z.object({
  MCode: z.string().min(1, "MCode는 필수입니다"),
  CcCode: z.string().min(1, "CcCode는 필수입니다"),
  UserId: z.string().min(1, "UserId는 필수입니다"),
  UKey: z.string().min(1, "UKey는 필수입니다"),
  AKey: z.string().min(1, "AKey는 필수입니다"),
});

// 화물맨은 COMCODE·Key·VariablePath가 서버 고정값(백엔드 appsettings)이라 업체별 화주 정보만 받는다.
const cargoManSchema = z.object({
  OwId: z.string().min(1, "화주 아이디는 필수입니다"),
  OwName: z.string().min(1, "화주명은 필수입니다"),
});

// --- Helpers ---

function getStatus(
  infos: CallPassAPIInfoDto[] | undefined,
  type: CallCenterType,
): "active" | "deleted" | "none" {
  const info = infos?.find((i) => i.callCenterType === type);
  if (!info) return "none";
  return info.isDeleted ? "deleted" : "active";
}

interface CallPassSettingPanelProps {
  companyGuid: string;
}

export function CallPassSettingPanel({
  companyGuid,
}: CallPassSettingPanelProps) {
  const { data: infos, isLoading } = useCallPassAPIInfos(companyGuid);
  const [selectedType, setSelectedType] = useState<CallCenterType | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-xs text-gray-400">로딩 중...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">
          정보망 API 설정
        </h4>
        <p className="text-xs text-gray-500">
          콜센터별 API 인증 정보를 등록/수정합니다
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="w-full">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="h-10 px-3 text-left text-xs font-medium uppercase text-gray-500">
                정보망
              </th>
              <th className="h-10 px-3 text-left text-xs font-medium uppercase text-gray-500">
                상태
              </th>
              <th className="h-10 px-3 text-right text-xs font-medium uppercase text-gray-500" />
            </tr>
          </thead>
          <tbody>
            {CALL_CENTER_TYPES.map((cc) => {
              const status = getStatus(infos, cc.value);
              const statusInfo = CALL_CENTER_STATUS_MAP[status];
              return (
                <tr
                  key={cc.value}
                  className="h-10 border-b last:border-b-0 hover:bg-gray-50"
                >
                  <td className="px-3 text-xs font-medium text-gray-700">
                    {cc.label}
                  </td>
                  <td className="px-3">
                    <span
                      className={`inline-block rounded-full px-1.5 py-0.5 text-xs ${statusInfo.color}`}
                    >
                      {statusInfo.label}
                    </span>
                  </td>
                  <td className="px-3 text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedType(cc.value)}
                      className="h-7 px-2 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      {status === "none" ? "등록" : "수정"}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedType && (
        <CallCenterForm
          key={`${companyGuid}-${selectedType}`}
          companyGuid={companyGuid}
          callCenterType={selectedType}
          existing={infos?.find((i) => i.callCenterType === selectedType)}
          onClose={() => setSelectedType(null)}
        />
      )}
    </div>
  );
}

// --- Per-CallCenter Form ---

interface CallCenterFormProps {
  companyGuid: string;
  callCenterType: CallCenterType;
  existing?: CallPassAPIInfoDto;
  onClose: () => void;
}

function CallCenterForm({
  companyGuid,
  callCenterType,
  existing,
  onClose,
}: CallCenterFormProps) {
  const userGuid = useAuthStore((s) => s.user?.userGuid ?? "");
  const upsertMutation = useUpsertCallPassAPIInfo(companyGuid);
  const [isEnabled, setIsEnabled] = useState(
    existing ? !existing.isDeleted : true,
  );
  const [showSecrets, setShowSecrets] = useState(false);

  const { data: decryptedData, isLoading: decrypting } = useDecryptedApiInfo(
    companyGuid,
    existing?.apiInfo,
  );

  if (decrypting) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <span className="text-xs text-gray-400">복호화 중...</span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-900">
            {CALL_CENTER_TYPES.find((c) => c.value === callCenterType)?.label}
          </span>
          <div className="flex items-center gap-1.5">
            <Switch
              checked={isEnabled}
              onCheckedChange={setIsEnabled}
              className="h-4 w-7"
            />
            <span className="text-xs text-gray-500">
              {isEnabled ? "사용" : "미사용"}
            </span>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="h-7 px-2"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={14} />
        </Button>
      </div>

      <div className="p-4">
        {callCenterType === "24시" && (
          <Cargo24Form
            companyGuid={companyGuid}
            defaultValues={(decryptedData ?? {}) as Partial<Cargo24Auth>}
            isEnabled={isEnabled}
            isPending={upsertMutation.isPending}
            showSecrets={showSecrets}
            onToggleSecrets={() => setShowSecrets((v) => !v)}
            onSubmit={async (formData) => {
              const jsonStr = JSON.stringify(formData);
              // 암호화 키는 설정 대상 배차 업체(companyGuid) 기준 — 서버가 CompanyGuid로 복호화 (조회/저장 통일)
              const encrypted = await encrypt(companyGuid, jsonStr);
              await upsertMutation.mutateAsync({
                CallCenterType: "24시",
                CompanyGuid: companyGuid,
                AuthUserGuid: userGuid,
                APIInfo: encrypted,
                IsDeleted: !isEnabled,
              });
              onClose();
            }}
          />
        )}
        {callCenterType === "원콜" && (
          <OneCallForm
            companyGuid={companyGuid}
            defaultValues={(decryptedData ?? {}) as Partial<OneCallAuth>}
            isEnabled={isEnabled}
            isPending={upsertMutation.isPending}
            showSecrets={showSecrets}
            onToggleSecrets={() => setShowSecrets((v) => !v)}
            onSubmit={async (formData) => {
              const jsonStr = JSON.stringify(formData);
              const encrypted = await encrypt(companyGuid, jsonStr);
              await upsertMutation.mutateAsync({
                CallCenterType: "원콜",
                CompanyGuid: companyGuid,
                AuthUserGuid: userGuid,
                APIInfo: encrypted,
                IsDeleted: !isEnabled,
              });
              onClose();
            }}
          />
        )}
        {callCenterType === "인성" && (
          <InsungForm
            companyGuid={companyGuid}
            defaultValues={(decryptedData ?? {}) as Partial<InsungAuth>}
            isEnabled={isEnabled}
            isPending={upsertMutation.isPending}
            showSecrets={showSecrets}
            onToggleSecrets={() => setShowSecrets((v) => !v)}
            onSubmit={async (formData) => {
              const jsonStr = JSON.stringify(formData);
              const encrypted = await encrypt(companyGuid, jsonStr);
              await upsertMutation.mutateAsync({
                CallCenterType: "인성",
                CompanyGuid: companyGuid,
                AuthUserGuid: userGuid,
                APIInfo: encrypted,
                IsDeleted: !isEnabled,
              });
              onClose();
            }}
          />
        )}
        {callCenterType === "화물맨" && (
          <CargoManForm
            companyGuid={companyGuid}
            defaultValues={(decryptedData ?? {}) as Partial<CargoManAuth>}
            isEnabled={isEnabled}
            isPending={upsertMutation.isPending}
            onSubmit={async (formData) => {
              const jsonStr = JSON.stringify(formData);
              const encrypted = await encrypt(companyGuid, jsonStr);
              await upsertMutation.mutateAsync({
                CallCenterType: "화물맨",
                CompanyGuid: companyGuid,
                AuthUserGuid: userGuid,
                APIInfo: encrypted,
                IsDeleted: !isEnabled,
              });
              onClose();
            }}
          />
        )}
        {callCenterType === "24시_AGENT" && (
          <Cargo24AgentForm
            isPending={upsertMutation.isPending}
            onSubmit={async () => {
              // AGENT 타입은 인증 정보가 없으나 { "Key": "empty" } 형태로 저장
              // 암호화 키는 설정 대상 배차 업체(companyGuid) 기준 — 서버가 CompanyGuid로 복호화 (조회/저장 통일)
              const encrypted = await encrypt(
                companyGuid,
                JSON.stringify({ Key: "empty" }),
              );
              await upsertMutation.mutateAsync({
                CallCenterType: "24시_AGENT",
                CompanyGuid: companyGuid,
                AuthUserGuid: userGuid,
                APIInfo: encrypted,
                IsDeleted: !isEnabled,
              });
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
}

// --- Cargo24 Agent Form (인증 정보 없음 — 사용 여부 토글만) ---

interface Cargo24AgentFormProps {
  isPending: boolean;
  onSubmit: () => Promise<void>;
}

function Cargo24AgentForm({ isPending, onSubmit }: Cargo24AgentFormProps) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
      className="flex flex-col gap-3"
    >
      <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
        Agent24 데스크톱 앱 연동 방식입니다. 별도의 API 인증 정보가 필요하지
        않으며, 상단 토글로 사용 여부만 설정합니다.
      </div>
      <div className="flex justify-end pt-1">
        <Button
          type="submit"
          size="sm"
          disabled={isPending}
          className="h-7 bg-blue-600 px-3 text-xs font-bold hover:bg-blue-700"
        >
          <HugeiconsIcon
            icon={CheckmarkCircle02Icon}
            size={14}
            className="mr-1"
          />
          {isPending ? "저장 중..." : "저장"}
        </Button>
      </div>
    </form>
  );
}

// --- Field helper ---

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

function Field({ label, required, error, children }: FieldProps) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-0.5 text-xs text-red-500">{error}</p>}
    </div>
  );
}

// --- Toggle secrets button ---

interface SecretToggleProps {
  show: boolean;
  onToggle: () => void;
}

function SecretToggle({ show, onToggle }: SecretToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="text-xs text-gray-500 hover:text-gray-700"
    >
      <HugeiconsIcon icon={show ? ViewOffIcon : ViewIcon} size={14} />
    </button>
  );
}

// --- Form footer ---

interface FormFooterProps {
  isPending: boolean;
  showSecrets: boolean;
  onToggleSecrets: () => void;
}

function FormFooter({
  isPending,
  showSecrets,
  onToggleSecrets,
}: FormFooterProps) {
  return (
    <div className="flex items-center justify-between pt-3">
      <SecretToggle show={showSecrets} onToggle={onToggleSecrets} />
      <Button
        type="submit"
        size="sm"
        disabled={isPending}
        className="h-7 bg-blue-600 px-3 text-xs font-bold hover:bg-blue-700"
      >
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={14}
          className="mr-1"
        />
        {isPending ? "저장 중..." : "저장"}
      </Button>
    </div>
  );
}

// --- Cargo24 Form ---

interface Cargo24FormProps {
  companyGuid: string;
  defaultValues: Partial<Cargo24Auth>;
  isEnabled: boolean;
  isPending: boolean;
  showSecrets: boolean;
  onToggleSecrets: () => void;
  onSubmit: (data: Cargo24Auth) => Promise<void>;
}

function Cargo24Form({
  defaultValues,
  isPending,
  showSecrets,
  onToggleSecrets,
  onSubmit,
}: Cargo24FormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Cargo24Auth>({
    resolver: zodResolver(cargo24Schema),
    defaultValues: {
      Key: defaultValues.Key ?? "",
      AESKey: defaultValues.AESKey ?? "",
      AESIV: defaultValues.AESIV ?? "",
    },
  });

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data))}
      className="flex flex-col gap-3"
    >
      <Field label="API Key" required error={errors.Key?.message}>
        <Input
          {...register("Key")}
          type={showSecrets ? "text" : "password"}
          placeholder="API Key"
          className="h-8 text-xs"
        />
      </Field>
      <Field label="AES Key" required error={errors.AESKey?.message}>
        <Input
          {...register("AESKey")}
          type={showSecrets ? "text" : "password"}
          placeholder="AES 암호화 Key"
          className="h-8 text-xs"
        />
      </Field>
      <Field label="AES IV" required error={errors.AESIV?.message}>
        <Input
          {...register("AESIV")}
          type={showSecrets ? "text" : "password"}
          placeholder="AES 초기화 벡터"
          className="h-8 text-xs"
        />
      </Field>
      <FormFooter
        isPending={isPending}
        showSecrets={showSecrets}
        onToggleSecrets={onToggleSecrets}
      />
    </form>
  );
}

// --- OneCall Form ---

interface OneCallFormProps {
  companyGuid: string;
  defaultValues: Partial<OneCallAuth>;
  isEnabled: boolean;
  isPending: boolean;
  showSecrets: boolean;
  onToggleSecrets: () => void;
  onSubmit: (data: OneCallAuth) => Promise<void>;
}

function OneCallForm({
  defaultValues,
  isPending,
  showSecrets,
  onToggleSecrets,
  onSubmit,
}: OneCallFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OneCallAuth>({
    resolver: zodResolver(oneCallSchema),
    defaultValues: {
      Key: defaultValues.Key ?? "",
      Id: defaultValues.Id ?? "",
      Password: defaultValues.Password ?? "",
    },
  });

  const rootError = errors.root?.message ?? (errors as Record<string, { message?: string }>)[""]?.message;

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data))}
      className="flex flex-col gap-3"
    >
      <Field label="Shipper Key" error={errors.Key?.message}>
        <Input
          {...register("Key")}
          type={showSecrets ? "text" : "password"}
          placeholder="기존 방식 Key (플랫폼 미사용 시)"
          className="h-8 text-xs"
        />
      </Field>
      <div className="flex items-center gap-2 py-1">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-[10px] text-gray-400">또는 플랫폼 계정</span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>
      <Field label="ID" error={errors.Id?.message}>
        <Input
          {...register("Id")}
          placeholder="플랫폼 사업자 ID"
          className="h-8 text-xs"
        />
      </Field>
      <Field label="Password" error={errors.Password?.message}>
        <Input
          {...register("Password")}
          type={showSecrets ? "text" : "password"}
          placeholder="플랫폼 사업자 Password"
          className="h-8 text-xs"
        />
      </Field>
      {rootError && (
        <p className="text-xs text-red-500">{rootError}</p>
      )}
      <FormFooter
        isPending={isPending}
        showSecrets={showSecrets}
        onToggleSecrets={onToggleSecrets}
      />
    </form>
  );
}

// --- Insung Form ---

interface InsungFormProps {
  companyGuid: string;
  defaultValues: Partial<InsungAuth>;
  isEnabled: boolean;
  isPending: boolean;
  showSecrets: boolean;
  onToggleSecrets: () => void;
  onSubmit: (data: InsungAuth) => Promise<void>;
}

function InsungForm({
  defaultValues,
  isPending,
  showSecrets,
  onToggleSecrets,
  onSubmit,
}: InsungFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InsungAuth>({
    resolver: zodResolver(insungSchema),
    defaultValues: {
      MCode: defaultValues.MCode ?? "",
      CcCode: defaultValues.CcCode ?? "",
      UserId: defaultValues.UserId ?? "",
      UKey: defaultValues.UKey ?? "",
      AKey: defaultValues.AKey ?? "",
    },
  });

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data))}
      className="flex flex-col gap-3"
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="MCode" required error={errors.MCode?.message}>
          <Input
            {...register("MCode")}
            placeholder="업체 코드"
            className="h-8 text-xs"
          />
        </Field>
        <Field label="CcCode" required error={errors.CcCode?.message}>
          <Input
            {...register("CcCode")}
            placeholder="콜센터 코드"
            className="h-8 text-xs"
          />
        </Field>
      </div>
      <Field label="User ID" required error={errors.UserId?.message}>
        <Input
          {...register("UserId")}
          placeholder="사용자 ID"
          className="h-8 text-xs"
        />
      </Field>
      <Field label="UKey" required error={errors.UKey?.message}>
        <Input
          {...register("UKey")}
          type={showSecrets ? "text" : "password"}
          placeholder="사용자 Key"
          className="h-8 text-xs"
        />
      </Field>
      <Field label="AKey" required error={errors.AKey?.message}>
        <Input
          {...register("AKey")}
          type={showSecrets ? "text" : "password"}
          placeholder="인증 Key"
          className="h-8 text-xs"
        />
      </Field>
      <FormFooter
        isPending={isPending}
        showSecrets={showSecrets}
        onToggleSecrets={onToggleSecrets}
      />
    </form>
  );
}

// --- CargoMan Form ---

interface CargoManFormProps {
  companyGuid: string;
  defaultValues: Partial<CargoManAuth>;
  isEnabled: boolean;
  isPending: boolean;
  onSubmit: (data: CargoManAuth) => Promise<void>;
}

function CargoManForm({
  defaultValues,
  isPending,
  onSubmit,
}: CargoManFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CargoManAuth>({
    resolver: zodResolver(cargoManSchema),
    defaultValues: {
      OwId: defaultValues.OwId ?? "",
      OwName: defaultValues.OwName ?? "",
    },
  });

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data))}
      className="flex flex-col gap-3"
    >
      <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
        COMCODE·Key·VariablePath는 서버 공통값이라 서버 설정에서 관리합니다.
        여기서는 업체별로 다른 화주 정보만 등록합니다.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="화주 아이디" required error={errors.OwId?.message}>
          <Input
            {...register("OwId")}
            placeholder="빽통 로그인 ID"
            className="h-8 text-xs"
          />
        </Field>
        <Field label="화주명" required error={errors.OwName?.message}>
          <Input
            {...register("OwName")}
            placeholder="개인이름 또는 업체명"
            className="h-8 text-xs"
          />
        </Field>
      </div>
      <p className="text-[11px] leading-relaxed text-gray-500">
        화주 아이디·화주명은 화물맨 담당자가 메일로 발급하며 테스트/운영 서버가
        따로라 운영 전환 시 다시 받아야 합니다.
      </p>
      <div className="flex justify-end pt-1">
        <Button
          type="submit"
          size="sm"
          disabled={isPending}
          className="h-7 bg-blue-600 px-3 text-xs font-bold hover:bg-blue-700"
        >
          <HugeiconsIcon
            icon={CheckmarkCircle02Icon}
            size={14}
            className="mr-1"
          />
          {isPending ? "저장 중..." : "저장"}
        </Button>
      </div>
    </form>
  );
}
